import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAsyncEffect from 'use-async-effect';
import classNames from 'classnames';
import { PrematchCatalogContext } from 'context/prematchCatalogContext';
import { getLeagues, getSports } from 'services/api/sportBetsService';
import { sportTranslationKey } from 'helpers/sportI18n';
import { getDefaultSportIconSrc } from 'helpers/sportIcon';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { CountryFlag } from 'components/TeamCrest/TeamCrest';
import type { League } from 'types/League';
import type { Sport } from 'types/Sport';
import { ModernSportSheet } from './ModernSportSheet';
import styles from './ModernSportNav.module.css';

const MAX_LEAGUES = 15;

export const leagueOfferPath = (sport: Sport, league: League) =>
  `/sports-betting/${sport.name}/${sport.id}/${league.name}/${league.id}`;

type Props = {
  /** Selected sport; the first sport when not given. */
  sportId?: number;
  /** Highlighted league chip. */
  leagueId?: number;
  /** Picking a sport calls this instead of opening its top league. */
  onSport?: (sport: Sport) => void;
  /** Hide the "All" chip (the country / league list). */
  hideAll?: boolean;
};

/** Modern layout: the sports as an icon rail with offer counts, and the
 *  leagues of the selected sport as chips under it. */
export const ModernSportNav = ({
  sportId,
  leagueId,
  onSport,
  hideAll,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lastPrematchPush } = useContext(PrematchCatalogContext);
  const bump = lastPrematchPush?.version ?? 0;
  const [sports, setSports] = useState<Sport[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const active = sports.find((s) => s.id === sportId) ?? sports[0];

  useAsyncEffect(
    async (isMounted) => {
      const s = await getSports(undefined).catch(() => []);
      if (isMounted()) setSports(Array.isArray(s) ? s : []);
    },
    [bump]
  );

  useAsyncEffect(
    async (isMounted) => {
      if (!active) return;
      const l = await getLeagues(active.id).catch(() => null);
      if (isMounted()) setLeagues(Array.isArray(l) ? l : []);
    },
    [active?.id, bump]
  );

  const pickSport = async (s: Sport) => {
    if (onSport) {
      onSport(s);
      return;
    }
    const l: League[] | null = await getLeagues(s.id).catch(() => null);
    if (l?.length) navigate(leagueOfferPath(s, l[0]));
    else navigate(`/sports-betting/sport/${s.name}/${s.id}`);
  };

  if (sports.length === 0) return <div className={styles.placeholder} />;

  return (
    <div className={styles.nav}>
      {sheetOpen && (
        <ModernSportSheet
          close={() => setSheetOpen(false)}
          sportId={active?.id}
          leagueId={leagueId}
        />
      )}
      <div className={styles.sports}>
        {sports.map((s) => (
          <button
            type="button"
            key={s.id}
            className={classNames(
              styles.sport,
              s.id === active?.id && styles.sportActive
            )}
            onClick={() => pickSport(s)}
          >
            <span className={styles.iconWrap}>
              <SportIcon
                sportId={s.id}
                className={styles.icon}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = getDefaultSportIconSrc();
                }}
              />
              {!!s.count && <span className={styles.count}>{s.count}</span>}
            </span>
            <span className={styles.name}>
              {t(sportTranslationKey(s.id), { defaultValue: s.name })}
            </span>
          </button>
        ))}
      </div>
      {active && leagues.length > 0 && (
        <div className={styles.leagues}>
          {!hideAll && (
            <button
              type="button"
              className={styles.chip}
              onClick={() => setSheetOpen(true)}
            >
              {t('casino_all')} ▾
            </button>
          )}
          {leagues.slice(0, MAX_LEAGUES).map((l) => (
            <button
              type="button"
              key={l.id}
              className={classNames(
                styles.chip,
                l.id === leagueId && styles.chipActive
              )}
              onClick={() => navigate(leagueOfferPath(active, l))}
            >
              {l.cc ? (
                <CountryFlag cc={l.cc} className={styles.chipFlag} />
              ) : (
                <SportIcon
                  sportId={active.id}
                  className={styles.chipIcon}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = getDefaultSportIconSrc();
                  }}
                />
              )}
              {l.display_name || l.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
